import { useMemo, useState } from "react";
import {
    AlertCircle,
    Award,
    ChevronRight,
    Clock,
    DollarSign,
    Layers,
    MessageCircle,
    MousePointerClick,
    Send,
    TrendingUp,
    Users,
} from "lucide-react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useFlowReport } from "../api/queries";
import { useDashboardStore } from "../store/dashboardStore";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { SectionCard } from "../components/SectionCard";
import { KpiCard } from "../components/KpiCard";
import { BadgeHi } from "../components/BadgeHi";
import { Segmented } from "../components/Segmented";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import {
    cur,
    deltaDir,
    fmt,
    fmtDelta,
    fmtPct,
} from "../lib/format";
import { correctFailureRate } from "../lib/numbers";
import type { FlowReportNode } from "../types/reports";
import { cn } from "@/lib/utils";

type NodesViewMode = "table" | "cards";

type BadgeColor =
    | "gray"
    | "blue"
    | "emerald"
    | "amber"
    | "rose"
    | "violet"
    | "slate";

// Maps API node types → badge color (same logic do protótipo).
function nodeColorByType(t?: string): BadgeColor {
    if (!t) return "gray";
    const s = t.toLowerCase();
    if (s.includes("trigger")) return "violet";
    if (
        s.includes("message") ||
        s.includes("photo") ||
        s.includes("video") ||
        s.includes("audio")
    )
        return "amber";
    if (s.includes("tag")) return "emerald";
    if (s.includes("go_to") || s.includes("goto")) return "rose";
    return "gray";
}

// Best label = previewTitle > first button > name > id
function getNodeLabel(n: FlowReportNode): string | null {
    if (n.previewTitle && n.previewTitle.trim()) return n.previewTitle.trim();
    if (
        Array.isArray(n.buttons) &&
        n.buttons[0]?.text &&
        n.buttons[0].text.trim()
    )
        return n.buttons[0].text.trim();
    return null;
}

export function FlowReportView({ flowId }: { flowId?: string }) {
    const setOpenNode = useDashboardStore((s) => s.setOpenNode);
    const breadcrumbs = useDashboardStore((s) => s.breadcrumbs);
    const { data, error, isLoading, refetch } = useFlowReport(flowId);
    const loading = isLoading && !data;
    const [nodesView, setNodesView] = useState<NodesViewMode>("table");

    const flow = data?.flow;
    const fallbackLabel =
        breadcrumbs[breadcrumbs.length - 1]?.label || "Flow";

    const nodesCount = data?.nodes?.length;
    const hero = (
        <PageHeader
            icon={MessageCircle}
            title={loading ? "Carregando…" : flow?.name || fallbackLabel}
            subtitle={
                flow
                    ? [
                          nodesCount != null ? `${nodesCount} nodes` : null,
                          flow.status ? `status ${flow.status}` : null,
                          flow.createdAt
                              ? `criado em ${flow.createdAt.slice(0, 10)}`
                              : null,
                      ]
                          .filter(Boolean)
                          .join(" · ")
                    : undefined
            }
        />
    );

    if (error && !data) {
        return (
            <PageShell hero={hero}>
                <ApiBanner error={error} onRetry={() => refetch()} />
            </PageShell>
        );
    }

    const k = data?.kpis;
    const nodes = data?.nodes ?? [];
    // Backend manda `revenueDaily`; mantemos `revenueByDay` como fallback legado.
    const revenueByDay = data?.revenueDaily ?? data?.revenueByDay ?? [];
    const insights = data?.insights ?? [];

    const revenueN = cur(k?.revenue);
    const sendsN = cur(k?.sends);
    const clicksN = cur(k?.clicks);
    const ctrN = cur(k?.ctr);
    const rpmN = cur(k?.rpm);
    const failuresN = cur(k?.failures);
    const failureRate = correctFailureRate(failuresN, sendsN);

    const sortedNodes = useMemo(
        () => [...nodes].sort((a, b) => (b.revenue || 0) - (a.revenue || 0)),
        [nodes]
    );

    // (Estatísticas de melhor/pior dia removidas — não fazem parte do
    // Resumo no novo layout. Resumo agora foca em Receita / Por lead /
    // Por conversão / RPM + Envios/Falhas/Cliques.)

    const chartData = revenueByDay.map((p) => ({
        d:
            typeof p.date === "string" && p.date.length >= 10
                ? p.date.slice(8, 10)
                : (p.date || "").slice(-2),
        v: p.revenue || 0,
    }));

    return (
        <PageShell hero={hero}>
            {/* KPI strip — 6 cards do funil: Receita, Entrou, Concluiu, Em Flow, Envios, Cliques.
                RPM e Falhas mudaram pro Resumo $ ao lado (não eram primários no mockup). */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <KpiCard
                    icon={DollarSign}
                    iconColor="text-emerald-500"
                    label="Receita"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            "$" +
                            Number(revenueN).toLocaleString("pt-BR", {
                                maximumFractionDigits: 2,
                            })
                        )
                    }
                    delta={fmtDelta(k?.revenue)}
                    deltaDir={deltaDir(k?.revenue)}
                />
                <KpiCard
                    icon={Users}
                    iconColor="text-blue-500"
                    label="Entrou"
                    value={loading ? <SkelText /> : fmt(k?.entered ?? 0)}
                />
                <KpiCard
                    icon={Award}
                    iconColor="text-violet-500"
                    label="Concluiu"
                    value={loading ? <SkelText /> : fmt(k?.completed ?? 0)}
                    sub={
                        k?.completionPct != null
                            ? `${k.completionPct.toFixed(1)}% conv`
                            : undefined
                    }
                />
                <KpiCard
                    icon={Clock}
                    iconColor="text-amber-500"
                    label="Em flow"
                    value={loading ? <SkelText /> : fmt(k?.inFlowNow ?? 0)}
                    sub="parados agora"
                />
                <KpiCard
                    icon={Send}
                    iconColor="text-blue-500"
                    label="Envios"
                    value={
                        loading ? <SkelText /> : fmt(Math.round(sendsN))
                    }
                    sub={`${fmtPct(failureRate)} falhou`}
                    delta={fmtDelta(k?.sends)}
                    deltaDir={deltaDir(k?.sends)}
                />
                <KpiCard
                    icon={MousePointerClick}
                    iconColor="text-violet-500"
                    label="Cliques"
                    value={
                        loading ? <SkelText /> : fmt(Math.round(clicksN))
                    }
                    sub={`CTR ${fmtPct(ctrN)}`}
                    delta={fmtDelta(k?.clicks)}
                    deltaDir={deltaDir(k?.clicks)}
                />
            </div>

            {/* Receita/dia (2/3) + Resumo $ (1/3) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SectionCard
                    icon={TrendingUp}
                    title="Receita / dia"
                    subtitle={`${revenueByDay.length} ponto${revenueByDay.length === 1 ? "" : "s"}`}
                    className="lg:col-span-2"
                >
                    {loading ? (
                        <DashSkeleton h={208} />
                    ) : chartData.length === 0 ? (
                        <div className="h-52 flex items-center justify-center text-sm text-gray-400">
                            Sem dados no período
                        </div>
                    ) : (
                        <div className="h-52">
                            <ResponsiveContainer>
                                <AreaChart
                                    data={chartData}
                                    margin={{
                                        top: 5,
                                        right: 5,
                                        bottom: 0,
                                        left: -10,
                                    }}
                                >
                                    <defs>
                                        <linearGradient
                                            id="gFlowReportRev"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor="#10b981"
                                                stopOpacity={0.25}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor="#10b981"
                                                stopOpacity={0}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#f1f5f9"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="d"
                                        tick={{
                                            fontSize: 11,
                                            fill: "#64748b",
                                        }}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        tick={{
                                            fontSize: 11,
                                            fill: "#64748b",
                                        }}
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(v: number) =>
                                            "$" +
                                            (v >= 1000
                                                ? (v / 1000).toFixed(0) + "k"
                                                : v)
                                        }
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            borderRadius: 8,
                                            border: "1px solid #e5e7eb",
                                            fontSize: 12,
                                        }}
                                        formatter={(value) => [
                                            "$" +
                                                Number(value ?? 0).toLocaleString(
                                                    "pt-BR",
                                                    {
                                                        maximumFractionDigits: 2,
                                                    }
                                                ),
                                            "Receita",
                                        ]}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="v"
                                        stroke="#10b981"
                                        strokeWidth={2}
                                        fill="url(#gFlowReportRev)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </SectionCard>

                <SectionCard icon={DollarSign} title="Resumo · $">
                    {loading ? (
                        <DashSkeleton h={208} />
                    ) : (
                        <div className="space-y-3 text-sm">
                            <SummaryRow
                                label="Total"
                                value={
                                    "$" +
                                    Number(revenueN).toLocaleString("pt-BR", {
                                        maximumFractionDigits: 2,
                                    })
                                }
                                tone="hl"
                            />
                            <SummaryRow
                                label="Por lead entrante"
                                value={
                                    "$" +
                                    Number(
                                        k?.revenuePerLead ?? 0
                                    ).toFixed(2)
                                }
                            />
                            <SummaryRow
                                label="Por conversão"
                                value={
                                    "$" +
                                    Number(
                                        k?.revenuePerConversion ?? 0
                                    ).toFixed(2)
                                }
                            />
                            <SummaryRow
                                label="RPM"
                                value={"$" + Number(rpmN).toFixed(2)}
                            />
                            <hr className="border-gray-100" />
                            <SummaryRow
                                label="Envios"
                                value={fmt(Math.round(sendsN))}
                            />
                            <SummaryRow
                                label="Falhas"
                                value={`${fmt(Math.round(failuresN))} · ${fmtPct(failureRate)}`}
                                tone="rose"
                            />
                            <SummaryRow
                                label="Cliques"
                                value={`${fmt(Math.round(clicksN))} · CTR ${fmtPct(ctrN)}`}
                                tone="blue"
                            />
                        </div>
                    )}
                </SectionCard>
            </div>

            {/* Insights */}
            {insights.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {insights.map((ins, i) => (
                        <div
                            key={i}
                            className={cn(
                                "border rounded-lg p-3 text-sm flex items-start gap-2",
                                ins.type === "warning"
                                    ? "bg-amber-50 border-amber-100 text-amber-900"
                                    : ins.type === "success"
                                      ? "bg-emerald-50 border-emerald-100 text-emerald-900"
                                      : ins.type === "error"
                                        ? "bg-rose-50 border-rose-100 text-rose-900"
                                        : "bg-blue-50 border-blue-100 text-blue-900"
                            )}
                        >
                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                                <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70 mb-0.5">
                                    {ins.type}
                                </p>
                                <span>{ins.text}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tabela / Cards de nodes */}
            <SectionCard
                icon={Layers}
                title="Nodes · ordenado por receita"
                subtitle={`${sortedNodes.length} node${sortedNodes.length === 1 ? "" : "s"}`}
                right={
                    <Segmented<NodesViewMode>
                        value={nodesView}
                        onChange={setNodesView}
                        options={[
                            { v: "table", l: "Tabela" },
                            { v: "cards", l: "Cards" },
                        ]}
                    />
                }
            >
                {loading ? (
                    <DashSkeleton h={320} />
                ) : sortedNodes.length === 0 ? (
                    <EmptyMsg />
                ) : nodesView === "cards" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {sortedNodes.map((n, i) => (
                            <NodePreviewCard
                                key={n.id}
                                node={n}
                                rank={i + 1}
                                onClick={() => {
                                    if (flowId) {
                                        setOpenNode({
                                            flowId,
                                            nodeId: n.id,
                                        });
                                    }
                                }}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="overflow-x-auto -mx-5">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider border-b border-gray-200">
                                    <th className="text-left px-5 py-2">#</th>
                                    <th className="text-left px-5 py-2">
                                        Node / texto
                                    </th>
                                    <th className="text-left px-5 py-2">
                                        Tipo
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        Envios
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        Cliques
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        CTR
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        Passou
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        Parado
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        Receita
                                    </th>
                                    <th className="text-right px-5 py-2">
                                        %Flow
                                    </th>
                                    <th className="text-right px-5 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sortedNodes.map((n, i) => {
                                    const label = getNodeLabel(n);
                                    return (
                                        <tr
                                            key={n.id}
                                            className={
                                                n.isRemoved
                                                    ? "bg-gray-50/70 text-gray-400 italic"
                                                    : "cursor-pointer hover:bg-gray-50"
                                            }
                                            onClick={() => {
                                                // Linha sintética não tem node real → não navega.
                                                if (n.isRemoved) return;
                                                if (flowId) {
                                                    setOpenNode({
                                                        flowId,
                                                        nodeId: n.id,
                                                    });
                                                }
                                            }}
                                        >
                                            <td className="px-5 py-2.5 text-xs font-mono tabular-nums text-gray-400">
                                                {String(i + 1).padStart(2, "0")}
                                            </td>
                                            <td className="px-5 py-2.5 max-w-md">
                                                {label ? (
                                                    <div className="flex flex-col">
                                                        <span
                                                            className="font-medium text-gray-800 truncate leading-tight"
                                                            title={label}
                                                        >
                                                            {label}
                                                        </span>
                                                        {n.name && (
                                                            <span className="text-[10px] font-mono text-gray-400 truncate leading-tight">
                                                                {n.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span
                                                        className="font-medium text-gray-800 truncate"
                                                        title={
                                                            n.name || n.id
                                                        }
                                                    >
                                                        {n.name || n.id}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-5 py-2.5">
                                                {n.type && (
                                                    <BadgeHi
                                                        color={nodeColorByType(
                                                            n.type
                                                        )}
                                                    >
                                                        {n.type}
                                                    </BadgeHi>
                                                )}
                                            </td>
                                            <td className="px-5 py-2.5 text-right font-mono tabular-nums text-xs text-gray-700">
                                                {n.sends != null
                                                    ? fmt(n.sends)
                                                    : "—"}
                                            </td>
                                            <td className="px-5 py-2.5 text-right font-mono tabular-nums text-xs text-blue-600">
                                                {n.clicks != null
                                                    ? fmt(n.clicks)
                                                    : "—"}
                                            </td>
                                            <td className="px-5 py-2.5 text-right font-mono tabular-nums text-xs text-gray-500">
                                                {n.ctr != null && n.ctr > 0
                                                    ? fmtPct(n.ctr)
                                                    : "—"}
                                            </td>
                                            <td className="px-5 py-2.5 text-right font-mono tabular-nums text-xs text-gray-700">
                                                {n.passed
                                                    ? fmt(n.passed)
                                                    : "—"}
                                            </td>
                                            <td className="px-5 py-2.5 text-right font-mono tabular-nums text-xs text-amber-600">
                                                {(() => {
                                                    // Backend manda `stuck`;
                                                    // mantemos `stuckLeads`
                                                    // como fallback legado.
                                                    const stuck =
                                                        n.stuck ??
                                                        n.stuckLeads ??
                                                        0;
                                                    return stuck > 0
                                                        ? fmt(stuck)
                                                        : "—";
                                                })()}
                                            </td>
                                            <td className="px-5 py-2.5 text-right font-semibold text-gray-900 font-mono tabular-nums">
                                                {n.revenue
                                                    ? "$" +
                                                      Number(
                                                          n.revenue
                                                      ).toLocaleString(
                                                          "pt-BR",
                                                          {
                                                              maximumFractionDigits: 2,
                                                          }
                                                      )
                                                    : "—"}
                                            </td>
                                            <td className="px-5 py-2.5 text-right font-mono tabular-nums text-xs text-gray-500">
                                                {n.revenueSharePct != null &&
                                                n.revenueSharePct > 0
                                                    ? `${n.revenueSharePct.toFixed(0)}%`
                                                    : "0%"}
                                            </td>
                                            <td className="px-5 py-2.5 text-right">
                                                {!n.isRemoved && (
                                                    <ChevronRight className="w-4 h-4 text-gray-400 inline" />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>
        </PageShell>
    );
}

// ── helpers ────────────────────────────────────────────────

interface SummaryRowProps {
    label: string;
    value: React.ReactNode;
    tone?: "hl" | "rose" | "emerald" | "blue";
}

function SummaryRow({ label, value, tone }: SummaryRowProps) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-gray-500 text-xs">{label}</span>
            <span
                className={cn(
                    "font-mono tabular-nums font-semibold shrink-0",
                    tone === "hl"
                        ? "text-emerald-600 text-base"
                        : tone === "rose"
                          ? "text-rose-600 text-xs"
                          : tone === "emerald"
                            ? "text-emerald-600 text-xs"
                            : tone === "blue"
                              ? "text-blue-600 text-xs"
                              : "text-gray-900 text-xs"
                )}
            >
                {value}
            </span>
        </div>
    );
}

function EmptyMsg() {
    return (
        <div className="text-center py-12">
            <AlertCircle className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sem nodes no período</p>
        </div>
    );
}

interface NodePreviewCardProps {
    node: FlowReportNode;
    rank: number;
    onClick: () => void;
}

// Card no estilo Messenger: header colorido por tipo + bubble com imagem/título/subtítulo + botões.
function NodePreviewCard({ node, rank, onClick }: NodePreviewCardProps) {
    // Linha sintética "Versões anteriores / nós removidos": não é um node real,
    // não navega — card compacto tracejado só pra reconciliar os envios.
    if (node.isRemoved) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3 flex flex-col justify-center min-h-[96px]">
                <span className="text-[11px] font-semibold text-gray-500">
                    {node.name}
                </span>
                <span className="text-[10px] text-gray-400 mt-1 font-mono tabular-nums">
                    ↑{fmt(node.sends || 0)} envios de versões antigas do flow
                </span>
            </div>
        );
    }

    const type = (node.type || "").toLowerCase();
    const isImage = type.includes("photo") || type.includes("image");
    const isVideo = type.includes("video");
    const isAudio = type.includes("audio");
    const isDoc = type.includes("document") || type.includes("file");
    const isMsg =
        type.includes("message") || isImage || isVideo || isAudio || isDoc;
    const isTrigger = type.includes("trigger");
    const isTag = type.includes("tag");
    const isGoto = type.includes("go_to") || type.includes("goto");
    const isWait = type.includes("wait") || type.includes("delay");

    const headerBg = isTrigger
        ? "bg-violet-500"
        : isTag
          ? "bg-emerald-500"
          : isGoto
            ? "bg-rose-500"
            : isWait
              ? "bg-slate-400"
              : isMsg
                ? "bg-blue-500"
                : "bg-gray-400";

    const hasActivity =
        (node.revenue || 0) > 0 ||
        (node.clicks || 0) > 0 ||
        (node.sends || 0) > 0;
    const stuck = node.stuck ?? node.stuckLeads ?? 0;
    const title = node.previewTitle?.trim();
    const subtitle = node.previewSubtitle?.trim();
    const buttons = node.buttons || [];

    return (
        <div className="relative">
            <div className="absolute -top-2 -left-2 z-10 inline-flex items-center justify-center w-7 h-7 bg-gray-900 text-white text-[11px] font-bold rounded-full shadow ring-2 ring-white font-mono tabular-nums">
                #{rank}
            </div>
            {stuck > 0 && (
                <div className="absolute -top-2 -right-2 z-10 inline-flex items-center gap-1 bg-amber-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow ring-2 ring-white">
                    <Users className="w-3 h-3" />
                    <span className="font-mono tabular-nums">
                        {fmt(stuck)}
                    </span>
                    parados
                </div>
            )}
            <button
                onClick={onClick}
                className={cn(
                    "w-full text-left rounded-xl border bg-white overflow-hidden transition-all border-gray-200 shadow-sm hover:shadow-lg hover:border-blue-300",
                    !hasActivity && "opacity-60"
                )}
            >
                {/* Header */}
                <div
                    className={cn(
                        headerBg,
                        "text-white px-2 py-1 flex items-center justify-between gap-1"
                    )}
                >
                    <span className="text-[9px] uppercase font-bold tracking-wider truncate">
                        {node.type || "node"}
                    </span>
                    <span className="text-[9px] font-mono opacity-70 shrink-0">
                        #{node.id}
                    </span>
                </div>

                {/* Body */}
                {isMsg ? (
                    <div className="p-2.5 bg-slate-50">
                        {(isImage || isVideo) && (
                            <div
                                className="rounded-md mb-2 overflow-hidden bg-slate-200 flex items-center justify-center"
                                style={{
                                    height: 96,
                                    background: node.previewMediaUrl
                                        ? undefined
                                        : isVideo
                                          ? "linear-gradient(135deg, #334155, #0f172a)"
                                          : "linear-gradient(135deg, #cbd5e1, #94a3b8)",
                                }}
                            >
                                {node.previewMediaUrl ? (
                                    <img
                                        src={node.previewMediaUrl}
                                        alt=""
                                        className="object-cover w-full h-full"
                                        onError={(e) => {
                                            (
                                                e.currentTarget as HTMLImageElement
                                            ).style.display = "none";
                                        }}
                                    />
                                ) : (
                                    <Layers
                                        className={cn(
                                            "w-6 h-6",
                                            isVideo
                                                ? "text-white"
                                                : "text-slate-500"
                                        )}
                                    />
                                )}
                            </div>
                        )}
                        <div className="bg-white rounded-md border border-gray-100 px-2.5 py-2">
                            {title ? (
                                <>
                                    <p className="text-[12px] font-bold text-gray-900 leading-snug line-clamp-2">
                                        {title}
                                    </p>
                                    {subtitle && (
                                        <p className="text-[11px] text-gray-600 leading-snug mt-1 line-clamp-3">
                                            {subtitle}
                                        </p>
                                    )}
                                    {node.name && (
                                        <p className="text-[9px] font-mono text-gray-400 mt-1.5 truncate">
                                            {node.name}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className="text-[12px] font-semibold text-gray-800 leading-snug line-clamp-2">
                                    {node.name || node.id}
                                </p>
                            )}
                            {buttons.length > 0 && (
                                <div className="mt-2 -mx-2.5 -mb-2 border-t border-gray-100">
                                    {buttons.slice(0, 3).map((b, i) => (
                                        <div
                                            key={i}
                                            className="text-center text-blue-600 text-[11px] font-medium py-1.5 border-b last:border-0 border-gray-100 truncate px-2"
                                        >
                                            {b.text}
                                        </div>
                                    ))}
                                    {buttons.length > 3 && (
                                        <div className="text-center text-gray-400 text-[9px] py-1 bg-gray-50">
                                            +{buttons.length - 3}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div
                        className={cn(
                            "p-2",
                            isTrigger
                                ? "bg-violet-50"
                                : isTag
                                  ? "bg-emerald-50"
                                  : isGoto
                                    ? "bg-rose-50"
                                    : isWait
                                      ? "bg-slate-50"
                                      : "bg-gray-50"
                        )}
                    >
                        <p className="text-[11px] font-semibold text-gray-800 truncate leading-tight">
                            {node.name || node.id}
                        </p>
                        <p className="text-[9px] text-gray-500 mt-1">
                            {isTrigger
                                ? "ponto de entrada"
                                : isTag
                                  ? "adiciona tag"
                                  : isGoto
                                    ? "vai pra outro flow"
                                    : isWait
                                      ? "aguarda interação"
                                      : "—"}
                        </p>
                    </div>
                )}

                {/* Metrics footer */}
                <div className="px-2.5 py-1.5 border-t border-gray-100 flex items-center justify-between gap-1 text-[10px]">
                    <div className="flex items-center gap-2 text-gray-500 font-mono tabular-nums min-w-0">
                        {(node.sends || 0) > 0 && (
                            <span title="envios">↑{fmt(node.sends)}</span>
                        )}
                        {(node.clicks || 0) > 0 && (
                            <span className="text-blue-600" title="cliques">
                                ◉{fmt(node.clicks)}
                            </span>
                        )}
                    </div>
                    {(node.revenue || 0) > 0 ? (
                        <span className="text-emerald-600 font-bold font-mono tabular-nums shrink-0 text-[12px]">
                            $
                            {Number(node.revenue).toLocaleString("pt-BR", {
                                maximumFractionDigits: 0,
                            })}
                        </span>
                    ) : (
                        !hasActivity && (
                            <span className="text-gray-300 text-[9px] uppercase tracking-wider shrink-0">
                                sem dados
                            </span>
                        )
                    )}
                </div>
            </button>
        </div>
    );
}
